import { Button, Stack, Typography } from '@mui/material';
import { ChevronLeftIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { isPublicApiError } from '@/api/errors';
import { ErrorFallbackSupportLink } from '@/components/Error/ErrorFallbackSupportLink';
import { cdnStaticPath } from '@/helpers/url';
import { ButtonLink } from '@/router/Link';
import {
  ErrorFallbackActions,
  ErrorCodeBox,
  ErrorContainer,
  ErrorContent,
  ErrorImage,
  MonospaceTypography,
} from './styled-components';
import { useErrorHandling } from './useErrorHandling';

export type DefaultErrorFallbackProps = {
  error: unknown;
  reset: () => void;
  ActionsComponent?: (actions: { back: ReactNode; retry: ReactNode }) => ReactNode;
};

const defaultActions: DefaultErrorFallbackProps['ActionsComponent'] = ({ back, retry }) => [back, retry];
const defaultRetryAction: DefaultErrorFallbackProps['ActionsComponent'] = ({ retry }) => retry;

export const DefaultErrorFallback = ({
  error,
  reset,
  ActionsComponent = defaultActions,
}: DefaultErrorFallbackProps) => (
  <ErrorContainer data-testid="error-boundary">
    <ErrorImage src={cdnStaticPath('static/images/not-found-illustration.svg')} role="presentation" />
    <DefaultErrorContent error={error} reset={reset} ActionsComponent={ActionsComponent} />
  </ErrorContainer>
);

export const DefaultErrorFallbackBasic = ({
  error,
  reset,
  ActionsComponent = defaultRetryAction,
}: DefaultErrorFallbackProps) => (
  <DefaultErrorContent error={error} reset={reset} ActionsComponent={ActionsComponent} />
);

function DefaultErrorContent({ error, reset, ActionsComponent }: Required<DefaultErrorFallbackProps>) {
  const { handleRetryClick } = useErrorHandling({ reset });

  return (
    <ErrorContent role="alert">
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h3" component="h2">
            Glitch in the Vector Field
          </Typography>
          <Typography variant="subtitle1" component="h3">
            A brief anomaly was detected here. We've logged the error, <wbr />
            and our crew is on the case.
          </Typography>
        </Stack>
        <ErrorCodeBox>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {String(error)}
          </Typography>
          {isPublicApiError(error) && error.traceId && (
            <MonospaceTypography>Trace ID: {error.traceId}</MonospaceTypography>
          )}
        </ErrorCodeBox>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          Please try again in a few moments. If this issue persists,{' '}
          <ErrorFallbackSupportLink>contact support</ErrorFallbackSupportLink> and provide the page URL.
        </Typography>
        <ErrorFallbackActions sx={{ containerType: 'inline-size' }}>
          <ActionsComponent
            back={
              <ButtonLink key="back" to="/" variant="text" size="small" startIcon={<ChevronLeftIcon size={16} />}>
                Back to Known Space
              </ButtonLink>
            }
            retry={
              <Button key="retry" variant="contained" size="small" onClick={handleRetryClick}>
                Try Again
              </Button>
            }
          />
        </ErrorFallbackActions>
      </Stack>
    </ErrorContent>
  );
}
